"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/design-system/utils/cn";
import { displayDate, isBeforeToday } from "@repo/design-system/utils/date-helpers";
import { combineEmploymentTypes } from "@repo/design-system/utils/helpers";
import { formatSalaryRow, getSalaryState } from "@repo/design-system/utils/salary-display";
import { Printer } from "lucide-react";
import { useEffect, useState } from "react";
import PrivacyNotice from "~/components/job-page/jobadtemplate/privacy-notice-eon";
import { Button } from "~/components/ui/button-eon";
import { LocationDisplay } from "~/components/ui/location-display-eon";
import ShareDropdown from "~/components/ui/share-dropdown-eon";
import { WatchJobButton } from "~/components/ui/watch-job-button-eon";
import { toSimilarJob } from "~/lib/utils/to-similar-job";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobadComponentProps } from "~/types/job";

// Splits the task HTML right before the contact block (lookahead keeps the
// matched line) so it can be rendered separately in the contact section.
export const SPLIT_REGEX = /(?=(?:<(?:b|strong)>\s*)?(?:För frågor om tjänsten|For questions regarding the position|For questions about the position, please contact:|För fackliga frågor|For questions about the position, please contact the recruiting manager:))/;
export default function JobAdTemplateEonSverige(t0) {
  const $ = _c(129);
  const {
    job
  } = t0;
  const t = useLanguage();
  const {
    content,
    data
  } = job;
  let t1;
  if ($[0] !== content.compensationObject || $[1] !== t) {
    const salaryState = getSalaryState(content.compensationObject);
    t1 = formatSalaryRow(salaryState, t, t.i18n);
    $[0] = content.compensationObject;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const salaryRow = t1;
  const [newTask, setNewTask] = useState(content.task);
  let t2;
  let t3;
  if ($[3] !== content.task) {
    t2 = () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content.task, "text/html");
      const paragraphs = Array.from(doc.querySelectorAll("p"));
      const inclusionParagraph = paragraphs.find(_temp);
      const benefitParagraph = paragraphs.find(_temp2);
      if (inclusionParagraph && benefitParagraph) {
        inclusionParagraph.remove();
        benefitParagraph.remove();
      }
      setNewTask(doc.body.innerHTML.split(SPLIT_REGEX)[0]);
    };
    t3 = [content.task];
    $[3] = content.task;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== data.title) {
    t4 = <h2 className="mb-4 font-bold text-4xl text-primary md:text-5xl">{data.title}</h2>;
    $[6] = data.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !data.locations || data.locations.length === 1 ? `${t.jobad_locationDisplay_single}: ` : `${t.jobad_locationDisplay_multi}: `;
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="font-medium" data-source="lc">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== job) {
    t7 = <span data-source="job"><LocationDisplay job={job} showAll={true} /></span>;
    $[10] = job;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="mb-10 text-lg">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("overflow-wrap-break-word print:break-inside-auto print:text-black", "[&_p+p]:mt-4", "[&_p:not(li>p)]:mb-4 [&_p:not(li>p)]:print:my-0 [&_p:not(li>p)]:print:p-0", "[&_h1]:mb-4 [&_h1]:print:my-0 [&_h1]:print:p-0", "[&_h2]:mb-4 [&_h2]:print:my-0 [&_h2]:print:p-0", "[&_h3]:mb-4 [&_h3]:print:my-0 [&_h3]:print:p-0", "[&_li]:mb-1.5", "[&_ul]:mt-4 [&_ul]:mb-8 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:print:my-0 [&_ul]:print:py-0");
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t.jobad_description_HL) {
    t10 = <h3 className="sr-only">{t.jobad_description_HL}</h3>;
    $[16] = t.jobad_description_HL;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== content.task || $[19] !== content.taskHL || $[20] !== newTask) {
    t11 = content.task && <div className="mt-6">{content.taskHL && <h3 className="font-bold" data-source="job">{content.taskHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: newTask
      }} data-source="job" /></div>;
    $[18] = content.task;
    $[19] = content.taskHL;
    $[20] = newTask;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== content.profile || $[23] !== content.profileHL) {
    t12 = content.profile && <div className="mt-6">{content.profileHL && <h3 className="font-bold" data-source="job">{content.profileHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: content.profile
      }} data-source="job" /></div>;
    $[22] = content.profile;
    $[23] = content.profileHL;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== content.offer || $[26] !== content.offerHL) {
    t13 = content.offer && <div className="mt-6">{content.offerHL && <h3 className="font-bold" data-source="job">{content.offerHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: content.offer
      }} data-source="job" /></div>;
    $[25] = content.offer;
    $[26] = content.offerHL;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <div className={t9}>{t10}{t11}{t12}{t13}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t.jobad_details_HL) {
    t15 = <h3 className="mb-6 font-bold text-2xl" data-source="lc">{t.jobad_details_HL}:</h3>;
    $[33] = t.jobad_details_HL;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t.jobad_details_contractType) {
    t16 = <th data-source="lc">{t.jobad_details_contractType}:</th>;
    $[35] = t.jobad_details_contractType;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== data.contract) {
    t17 = <td data-source="job">{data.contract}</td>;
    $[37] = data.contract;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <tr>{t16}{t17}</tr>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t.jobad_details_employmentType) {
    t19 = <th data-source="lc">{t.jobad_details_employmentType}:</th>;
    $[42] = t.jobad_details_employmentType;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== data.employmentType || $[45] !== t) {
    t20 = combineEmploymentTypes(data.employmentType, t);
    $[44] = data.employmentType;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20) {
    t21 = <td data-source="job">{t20}</td>;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t19 || $[50] !== t21) {
    t22 = <tr>{t19}{t21}</tr>;
    $[49] = t19;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t.jobad_details_remote) {
    t23 = <th data-source="lc">{t.jobad_details_remote}:</th>;
    $[52] = t.jobad_details_remote;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== data.remote) {
    t24 = <td data-source="job">{data.remote}</td>;
    $[54] = data.remote;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <tr>{t23}{t24}</tr>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t.jobad_details_jobNumber) {
    t26 = <th data-source="lc">{t.jobad_details_jobNumber}:</th>;
    $[59] = t.jobad_details_jobNumber;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== data.jobNumber) {
    t27 = <td data-source="job">{data.jobNumber}</td>;
    $[61] = data.jobNumber;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27) {
    t28 = <tr>{t26}{t27}</tr>;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== salaryRow) {
    t29 = salaryRow && <tr><th data-source="lc">{salaryRow.label}:</th><td data-source="job">{salaryRow.value}</td></tr>;
    $[66] = salaryRow;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== data.applicationEnd || $[69] !== t) {
    t30 = data.applicationEnd && !isBeforeToday(data.applicationEnd) && <tr><th data-source="lc">{t.jobad_details_applicationDeadlineTitle}:</th><td data-source="job">{displayDate(data.applicationEnd.slice(0, 10), t)}</td></tr>;
    $[68] = data.applicationEnd;
    $[69] = t;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t18 || $[72] !== t22 || $[73] !== t25 || $[74] !== t28 || $[75] !== t29 || $[76] !== t30) {
    t31 = <table className="wrap-break-word mb-8 w-full text-left md:w-2/3 lg:w-full lg:table-fixed"><tbody className="mb-4">{t18}{t22}{t25}{t28}{t29}{t30}</tbody></table>;
    $[71] = t18;
    $[72] = t22;
    $[73] = t25;
    $[74] = t28;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== salaryRow || $[79] !== t.salary_info_text_additional) {
    t32 = salaryRow && <p data-source="lc">{t.salary_info_text_additional}</p>;
    $[78] = salaryRow;
    $[79] = t.salary_info_text_additional;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== data.applicationEnd || $[82] !== t.sverige_applicationDeadline) {
    t33 = (!data.applicationEnd || isBeforeToday(data.applicationEnd)) && <p data-source="lc">{t.sverige_applicationDeadline}</p>;
    $[81] = data.applicationEnd;
    $[82] = t.sverige_applicationDeadline;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <PrivacyNotice className="mt-4" />;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t15 || $[86] !== t31 || $[87] !== t32 || $[88] !== t33) {
    t35 = <section className="rounded bg-card p-10 leading-6 [&_td]:py-2 [&_td]:pl-2 [&_th]:py-2 [&_th]:align-text-top [&_th]:font-normal">{t15}{t31}{t32}{t33}{t34}</section>;
    $[85] = t15;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== data) {
    t36 = toSimilarJob(data);
    $[90] = data;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== t36) {
    t37 = <WatchJobButton className="h-full w-full" jobData={t36} variant="purple" />;
    $[92] = t36;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] !== job) {
    t38 = <ShareDropdown className="h-full w-full" job={job} variant="purple" />;
    $[94] = job;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Printer className="size-6" />;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  let t40;
  if ($[97] !== t.jobad_print) {
    t40 = <Button aria-label={t.jobad_print} className="h-full w-full" onClick={_temp3} variant="purple">{t39}</Button>;
    $[97] = t.jobad_print;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t37 || $[100] !== t38 || $[101] !== t40) {
    t41 = <section className="grid h-10 grid-flow-col grid-cols-3 gap-4 text-white">{t37}{t38}{t40}</section>;
    $[99] = t37;
    $[100] = t38;
    $[101] = t40;
    $[102] = t41;
  } else {
    t41 = $[102];
  }
  let t42;
  if ($[103] !== t.jobad_inclusionHL) {
    t42 = <p className="font-bold" data-source="lc">{t.jobad_inclusionHL}</p>;
    $[103] = t.jobad_inclusionHL;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  let t43;
  if ($[105] !== t.sverige_inclusion_text) {
    t43 = <p data-source="lc">{t.sverige_inclusion_text}</p>;
    $[105] = t.sverige_inclusion_text;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  let t44;
  if ($[107] !== t42 || $[108] !== t43) {
    t44 = <article className="rounded bg-card p-10">{t42}{t43}</article>;
    $[107] = t42;
    $[108] = t43;
    $[109] = t44;
  } else {
    t44 = $[109];
  }
  let t45;
  if ($[110] !== t.sverige_teaser_benefits) {
    t45 = <p className="font-bold" data-source="lc">{t.sverige_teaser_benefits}</p>;
    $[110] = t.sverige_teaser_benefits;
    $[111] = t45;
  } else {
    t45 = $[111];
  }
  let t46;
  if ($[112] !== t.sverige_benefits_text) {
    t46 = <p data-source="lc">{t.sverige_benefits_text}</p>;
    $[112] = t.sverige_benefits_text;
    $[113] = t46;
  } else {
    t46 = $[113];
  }
  let t47;
  if ($[114] !== t45 || $[115] !== t46) {
    t47 = <article className="rounded bg-primary p-10 text-white">{t45}{t46}</article>;
    $[114] = t45;
    $[115] = t46;
    $[116] = t47;
  } else {
    t47 = $[116];
  }
  let t48;
  if ($[117] !== t35 || $[118] !== t41 || $[119] !== t44 || $[120] !== t47) {
    t48 = <div className="flex flex-col gap-4">{t35}{t41}{t44}{t47}</div>;
    $[117] = t35;
    $[118] = t41;
    $[119] = t44;
    $[120] = t47;
    $[121] = t48;
  } else {
    t48 = $[121];
  }
  let t49;
  if ($[122] !== t14 || $[123] !== t48) {
    t49 = <div className="grid-cols-[minmax(0,1fr)_400px] gap-x-24 text-xl md:text-lg lg:grid xl:gap-x-32">{t14}{t48}</div>;
    $[122] = t14;
    $[123] = t48;
    $[124] = t49;
  } else {
    t49 = $[124];
  }
  let t50;
  if ($[125] !== t4 || $[126] !== t49 || $[127] !== t8) {
    t50 = <section className="outer-container py-container" id="job"><div className="inner-container">{t4}{t8}{t49}</div></section>;
    $[125] = t4;
    $[126] = t49;
    $[127] = t8;
    $[128] = t50;
  } else {
    t50 = $[128];
  }
  return t50;
}
function _temp3() {
  return window.print();
}
function _temp2(p_0) {
  return p_0.textContent?.includes("Vi arbetar f\xF6r ett mer");
}
function _temp(p) {
  return p.textContent?.includes("P\xE5 E.ON arbetar vi aktivt med inkludering");
}